import { ReactConfigurationGeneratorApi } from '@ingenico/react-configuration-generator';
import { injectable } from 'inversify';
import { ComponentType, ForwardRefExoticComponent, MemoExoticComponent, RefAttributes } from 'react';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { ConfigurationFormBuilderWrapperProps } from '../../components/configuration-form-builder-wrapper/ConfigurationFormBuilderWrapper.ctrl';
import { ExposedConfigurationFormBuilderWrapper } from '../../components/exposed-configuration-form-builder-wrapper/ExposedConfigurationFormBuilderWrapper';
import { ExposedLogicalDeviceBlockDeviceModal } from '../../components/exposed-logical-device-block-device-modal/ExposedLogicalDeviceBlockDeviceModal';
import { ExposedLogicalDeviceAttachedDevicePanel } from '../../components/exposed-logical-device-attached-device-panel/ExposedLogicalDeviceAttachedDevicePanel';
import { ExposedLogicalDeviceLogs } from '../../components/exposed-logical-device-logs/ExposedLogicalDeviceLogs';
import { ExposedLogicalDeviceConfigurationTab } from '../../components/exposed-logical-device-configuration-tab/ExposedLogicalDeviceConfigurationTab';
import {
  ExposedLogicalDeviceCallLogPage,
  ExposedLogicalDeviceDiagnosticPage,
  ExposedLogicalDeviceDiagnosticsTab,
  ExposedLogicalDeviceHistoryTab,
} from '../../components/exposed-logical-device-operations/ExposedLogicalDeviceOperations';
import { ExposedLogicalDeviceScreenSharePage } from '../../components/exposed-logical-device-screen-share-page/ExposedLogicalDeviceScreenSharePage';
import { ExposedLogicalDeviceSyncConfigurationModal } from '../../components/exposed-logical-device-sync-configuration-modal/ExposedLogicalDeviceSyncConfigurationModal';
import { ExposedLogicalDeviceViewOutputModal } from '../../components/exposed-logical-device-view-output-modal/ExposedLogicalDeviceViewOutputModal';
import { ExposedTargetDeviceWrapper } from '../../components/exposed-target-device-wrapper/ExposedTargetDeviceWrapper';
import { ParameterModuleTab } from '../../components/parameter-module/parameter-module-tab/ParameterModuleTab.ctrl';
import type { ParameterModuleTabProps } from '../../components/parameter-module/parameter-module-tab/ParameterModuleTab.model';
import type { ExposedTargetDeviceWrapperProps } from '../models/exposedTargetDeviceWrapper.model';
import type { DeviceManagementApiPort } from './ports/api.model';
import { ExposedConfigurationFormBuilderWrapperV2 } from '../../components/exposed-configuration-form-builder-wrapper/ExposedConfigurationFormBuilderWrapperV2';
import { ExposedProvisioningDeviceChangeDetailsDrawer } from '../../components/exposed-device-change-details-page-drawer/ExposedProvisioningDeviceChangeDetailsDrawer.ctrl';
import { ExposedLogicalDeviceBlockDeviceModalV2 } from '../../components/exposed-logical-device-block-device-modal-v2/ExposedLogicalDeviceBlockDeviceModal';
import { ExposedSyncConfigurationMerchantModal } from '../../components/exposed-sync-configuration-merchant-modal/ExposedSyncConfigurationMerchantModal.ctrl';
import { ExposedSyncConfigurationStoreModal } from '../../components/exposed-sync-configuration-store-modal/ExposedSyncConfigurationStoreModal.ctrl';

@injectable()
export class DeviceManagementApi implements DeviceManagementApiPort {
  public exposeConfigurationFormBuilderComponent(): Observable<
    MemoExoticComponent<
      ForwardRefExoticComponent<ConfigurationFormBuilderWrapperProps & RefAttributes<ReactConfigurationGeneratorApi>>
    >
  > {
    return of(ExposedConfigurationFormBuilderWrapper);
  }

  public exposeTargetDeviceComponent(): Observable<
    MemoExoticComponent<ComponentType<ExposedTargetDeviceWrapperProps>>
  > {
    return of(ExposedTargetDeviceWrapper);
  }

  public exposeParameterModuleTabComponent(): Observable<
    MemoExoticComponent<ForwardRefExoticComponent<ParameterModuleTabProps>>
  > {
    return of(ParameterModuleTab as MemoExoticComponent<ForwardRefExoticComponent<ParameterModuleTabProps>>);
  }

  public exposeLogicalDeviceSyncConfigurationModal(): Observable<typeof ExposedLogicalDeviceSyncConfigurationModal> {
    return of(ExposedLogicalDeviceSyncConfigurationModal);
  }

  public exposeLogicalDeviceBlockDeviceModal(): Observable<typeof ExposedLogicalDeviceBlockDeviceModal> {
    return of(ExposedLogicalDeviceBlockDeviceModal);
  }

  public exposeLogicalDeviceViewOutputModal(): Observable<typeof ExposedLogicalDeviceViewOutputModal> {
    return of(ExposedLogicalDeviceViewOutputModal);
  }

  public exposeLogicalDeviceScreenSharePage(): Observable<typeof ExposedLogicalDeviceScreenSharePage> {
    return of(ExposedLogicalDeviceScreenSharePage);
  }

  public exposeLogicalDeviceAttachedDevicePanel(): Observable<typeof ExposedLogicalDeviceAttachedDevicePanel> {
    return of(ExposedLogicalDeviceAttachedDevicePanel);
  }

  public exposeLogicalDeviceHistoryTab(): Observable<typeof ExposedLogicalDeviceHistoryTab> {
    return of(ExposedLogicalDeviceHistoryTab);
  }

  public exposeLogicalDeviceConfigurationTab(): Observable<typeof ExposedLogicalDeviceConfigurationTab> {
    return of(ExposedLogicalDeviceConfigurationTab);
  }

  public exposeLogicalDeviceDiagnosticsTab(): Observable<typeof ExposedLogicalDeviceDiagnosticsTab> {
    return of(ExposedLogicalDeviceDiagnosticsTab);
  }

  public exposeLogicalDeviceCallLogPage(): Observable<typeof ExposedLogicalDeviceCallLogPage> {
    return of(ExposedLogicalDeviceCallLogPage);
  }

  public exposeLogicalDeviceDiagnosticPage(): Observable<typeof ExposedLogicalDeviceDiagnosticPage> {
    return of(ExposedLogicalDeviceDiagnosticPage);
  }

  public exposeLogicalDeviceLogs(): Observable<typeof ExposedLogicalDeviceLogs> {
    return of(ExposedLogicalDeviceLogs);
  }

  public exposeLogicalDeviceConfigurationTabV2(): Observable<typeof ExposedConfigurationFormBuilderWrapperV2> {
    return of(ExposedConfigurationFormBuilderWrapperV2);
  }

  public exposeLogicalDeviceBlockDeviceModalV2(): Observable<typeof ExposedLogicalDeviceBlockDeviceModalV2> {
    return of(ExposedLogicalDeviceBlockDeviceModalV2);
  }

  public exposeDeviceChangeDetailsDrawer(): Observable<typeof ExposedProvisioningDeviceChangeDetailsDrawer> {
    return of(ExposedProvisioningDeviceChangeDetailsDrawer);
  }

  public exposeSyncConfigurationMerchantModal(): Observable<typeof ExposedSyncConfigurationMerchantModal> {
    return of(ExposedSyncConfigurationMerchantModal);
  }

  public exposeSyncConfigurationStoreModal(): Observable<typeof ExposedSyncConfigurationStoreModal> {
    return of(ExposedSyncConfigurationStoreModal);
  }
}
